// The sound the bell makes when something new arrives.
//
// Synthesised rather than an audio file: it's two short sine tones, and a
// bundled mp3 would be a binary asset in the repo, a network request, and a
// decode — for a third of a second of beep.
//
// Browsers refuse to start audio until the page has been interacted with, so
// the context is created lazily and primed on the first click (see below). If
// it is still blocked, the chime is silently skipped — a notification that is
// visible but quiet is fine; an exception thrown out of a poll is not.

let context = null;

function audioContext() {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!context) context = new Ctx();
    // Suspended is the normal state before the page has been interacted with,
    // and again after some browsers idle it out.
    if (context.state === 'suspended') context.resume().catch(() => {});
    return context;
}

// Two rising notes — enough to notice across a room, short enough not to be
// the thing everyone in the office hates by Wednesday.
const NOTES = [
    { frequency: 880, at: 0 }, // A5
    { frequency: 1174.66, at: 0.11 }, // D6
];

const PEAK_GAIN = 0.18;
const NOTE_LENGTH = 0.28;

export function playChime() {
    try {
        const ctx = audioContext();
        if (!ctx) return;
        const now = ctx.currentTime;

        NOTES.forEach(({ frequency, at }) => {
            const oscillator = ctx.createOscillator();
            const gain = ctx.createGain();
            oscillator.type = 'sine';
            oscillator.frequency.value = frequency;

            const start = now + at;
            // Ramped rather than switched on: a square-edged start on a sine
            // tone clicks. Exponential ramps can't touch zero, hence 0.0001.
            gain.gain.setValueAtTime(0.0001, start);
            gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + NOTE_LENGTH);

            oscillator.connect(gain);
            gain.connect(ctx.destination);
            oscillator.start(start);
            oscillator.stop(start + NOTE_LENGTH + 0.02);
        });
    } catch {
        // No audio on this device, or the browser refused. Not worth a word.
    }
}

/**
 * Wake the audio context on the first interaction of the session.
 *
 * Without this the very first chime is the one that gets swallowed — the
 * notification that arrives while the admin is reading, before they have
 * clicked anything, is exactly the one worth hearing.
 */
export function primeChime() {
    const wake = () => {
        audioContext();
        window.removeEventListener('pointerdown', wake);
        window.removeEventListener('keydown', wake);
    };
    window.addEventListener('pointerdown', wake);
    window.addEventListener('keydown', wake);
    return () => {
        window.removeEventListener('pointerdown', wake);
        window.removeEventListener('keydown', wake);
    };
}
